import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";

// Template lives in public/ and uses default {tag} delimiters (note: the Health Check
// exporter uses [[ ]] — this template does not).
const TEMPLATE_URL = "/riskassessment/ra-template.docx";

export interface RiskAssessmentHazard {
    name?: string;
    description?: string;
    peopleAtRisk?: string[];
    controlsInPlace?: string[];
    additionalControls?: string[];
    severity?: number | null;
    likelihood?: number | null;
    activityTask?: string;
}

export interface RiskAssessment {
    title?: string;
    description?: string;
    activity?: string;
    location?: string;
    dateAssessed?: string;
    assessedBy?: string;
    reviewDate?: string;
    reference?: string;
    hazards?: RiskAssessmentHazard[];
}

const bulletList = (arr?: string[]): string =>
    (arr || []).filter(Boolean).map((c) => `• ${c}`).join("\n");

const toTemplateData = (a: RiskAssessment) => ({
    title: a.title || "Risk Assessment",
    location: a.location || "",
    dateAssessed: a.dateAssessed || "",
    assessedBy: a.assessedBy || "",
    activity: a.activity || "",
    reviewDate: a.reviewDate || "",
    reference: a.reference || "",
    hazards: (a.hazards || []).map((h) => ({
        activityTask: h.activityTask || a.activity || "",
        name: h.name || "",
        personsAtRisk: (h.peopleAtRisk || []).join("\n"),
        controlsInPlace: bulletList(h.controlsInPlace),
        // Severity / Likelihood / Risk are intentionally left blank for the competent
        // assessor to complete — never auto-filled from the AI.
        severity: "",
        likelihood: "",
        risk: "",
        additionalControls: bulletList(h.additionalControls),
    })),
});

export const generateRiskAssessmentDoc = async (assessment: RiskAssessment): Promise<void> => {
    // cache-bust so a freshly deployed template is always picked up
    const response = await fetch(`${TEMPLATE_URL}?t=${new Date().getTime()}`, { cache: "no-store" });
    if (!response.ok) {
        throw new Error(`Could not load Word template (${response.status})`);
    }
    const content = await response.arrayBuffer();
    const zip = new PizZip(content);
    const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });
    try {
        doc.render(toTemplateData(assessment));
    } catch (renderErr) {
        // docxtemplater throws on template-tag mismatches; log the underlying detail
        // (otherwise opaque) before surfacing a friendly error to the caller.
        console.error("Risk assessment: Word template render failed", renderErr);
        throw renderErr;
    }
    const blob = doc.getZip().generate({
        type: "blob",
        mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    const safe = (assessment.title || "risk-assessment")
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
    saveAs(blob, `${safe || "risk-assessment"}.docx`);
};
